import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
  Legend, LineChart, Line, PieChart, Pie, Cell, CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import api from '../lib/api';
import { fmtMoney } from '../lib/format';

const C_PRIMARY = 'hsl(145 72% 40%)';
const C_INFO = 'hsl(199 60% 45%)';
const C_WARNING = 'hsl(38 92% 50%)';
const C_MUTED = 'hsl(215 14% 55%)';
const C_GRID = 'hsl(220 14% 26%)';

const tooltipStyle = {
  backgroundColor: 'hsl(220 18% 18%)',
  border: '1px solid hsl(220 14% 26%)',
  borderRadius: 8,
  fontSize: 12,
  color: 'hsl(210 20% 92%)',
};

const monthLabel = (key) => {
  const [y, m] = key.split('-');
  const nombres = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${nombres[Number(m) - 1]} ${String(y).slice(2)}`;
};

export default function ReportesPage() {
  const [months, setMonths] = useState('6');

  const { data, isLoading } = useQuery({
    queryKey: ['reports-monthly', months],
    queryFn: async () => (await api.get('/reports/monthly', { params: { months: Number(months) } })).data,
  });

  const serie = (data?.serie || []).map((s) => ({ ...s, mesLabel: monthLabel(s.mes) }));
  const distribucion = (data?.distribucion || []).filter((d) => d.value > 0);
  const totalCotizado = distribucion.reduce((acc, d) => acc + d.value, 0);
  const totalCotizaciones = serie.reduce((acc, s) => acc + s.cotizaciones, 0);
  const totalFinalizadas = serie.reduce((acc, s) => acc + s.finalizadas, 0);
  const hayDatos = serie.some((s) => s.prefab || s.pv || s.contenedores || s.cotizaciones);

  return (
    <div className="space-y-5" data-testid="reportes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Reportes mensuales</h2>
          <p className="text-sm text-muted-foreground">Montos cotizados y actividad por módulo</p>
        </div>
        <Select value={months} onValueChange={setMonths}>
          <SelectTrigger className="w-[170px]" data-testid="report-months-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="3">Últimos 3 meses</SelectItem>
            <SelectItem value="6">Últimos 6 meses</SelectItem>
            <SelectItem value="12">Últimos 12 meses</SelectItem>
            <SelectItem value="24">Últimos 24 meses</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-72" /><Skeleton className="h-72" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-border shadow-[var(--shadow-elev-1)]">
              <CardHeader className="pb-1"><CardTitle className="text-[12px] uppercase tracking-wide text-muted-foreground font-medium">Total cotizado</CardTitle></CardHeader>
              <CardContent><div className="money text-xl font-semibold text-primary" data-testid="kpi-total-cotizado">{fmtMoney(totalCotizado)}</div></CardContent>
            </Card>
            <Card className="border-border shadow-[var(--shadow-elev-1)]">
              <CardHeader className="pb-1"><CardTitle className="text-[12px] uppercase tracking-wide text-muted-foreground font-medium">Cotizaciones</CardTitle></CardHeader>
              <CardContent><div className="money text-xl font-semibold" data-testid="kpi-total-cotizaciones">{totalCotizaciones}</div></CardContent>
            </Card>
            <Card className="border-border shadow-[var(--shadow-elev-1)]">
              <CardHeader className="pb-1"><CardTitle className="text-[12px] uppercase tracking-wide text-muted-foreground font-medium">Finalizadas</CardTitle></CardHeader>
              <CardContent><div className="money text-xl font-semibold" data-testid="kpi-finalizadas">{totalFinalizadas}</div></CardContent>
            </Card>
            <Card className="border-border shadow-[var(--shadow-elev-1)]">
              <CardHeader className="pb-1"><CardTitle className="text-[12px] uppercase tracking-wide text-muted-foreground font-medium">Tasa de cierre</CardTitle></CardHeader>
              <CardContent><div className="money text-xl font-semibold" data-testid="kpi-tasa-cierre">{totalCotizaciones ? `${((totalFinalizadas / totalCotizaciones) * 100).toFixed(0)}%` : '—'}</div></CardContent>
            </Card>
          </div>

          {!hayDatos ? (
            <Card className="border-border"><CardContent className="py-16 text-center text-muted-foreground" data-testid="reportes-empty">Sin datos para el periodo seleccionado</CardContent></Card>
          ) : (
            <>
              <Card className="border-border shadow-[var(--shadow-elev-1)]" data-testid="chart-montos-mensuales">
                <CardHeader className="pb-2"><CardTitle className="text-base">Monto cotizado por mes y módulo</CardTitle></CardHeader>
                <CardContent className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={serie} margin={{ top: 8, right: 12, left: 12, bottom: 0 }}>
                      <CartesianGrid stroke={C_GRID} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="mesLabel" stroke={C_MUTED} fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke={C_MUTED} fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <RTooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtMoney(v), name]} cursor={{ fill: 'hsl(220 16% 20% / 0.5)' }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="prefab" name="Prefab" stackId="a" fill={C_PRIMARY} radius={[0, 0, 0, 0]} />
                      <Bar dataKey="pv" name="Fotovoltaico" stackId="a" fill={C_WARNING} />
                      <Bar dataKey="contenedores" name="Contenedores" stackId="a" fill={C_INFO} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card className="border-border shadow-[var(--shadow-elev-1)]" data-testid="chart-actividad">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Actividad: cotizaciones vs finalizadas</CardTitle></CardHeader>
                  <CardContent className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={serie} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={C_GRID} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="mesLabel" stroke={C_MUTED} fontSize={11} tickLine={false} axisLine={false} />
                        <YAxis stroke={C_MUTED} fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                        <RTooltip contentStyle={tooltipStyle} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="cotizaciones" name="Cotizaciones" stroke={C_INFO} strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="finalizadas" name="Finalizadas" stroke={C_PRIMARY} strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="border-border shadow-[var(--shadow-elev-1)]" data-testid="chart-distribucion">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Distribución por módulo</CardTitle></CardHeader>
                  <CardContent className="h-[280px]">
                    {distribucion.length === 0 ? (
                      <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Sin datos para el periodo seleccionado</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={distribucion} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={3} strokeWidth={0}>
                            {distribucion.map((d, i) => (
                              <Cell key={d.name} fill={[C_PRIMARY, C_WARNING, C_INFO][i % 3]} />
                            ))}
                          </Pie>
                          <RTooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtMoney(v), name]} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
